import React, { useState, useEffect } from 'react';
import { Copy, Check, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Loader from '../../Loader';
import styles from './MarkdownRenderer.module.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { ghcolors  as theme } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import 'katex/dist/katex.min.css';
import ContentSkeleton from '../ContentSkeleton'
import ContentHeader from '../ContentHeader'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
  },
};

const MarkdownRenderer = ({ contentDetails, viewerUrl }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await fetch(viewerUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch content');
        }
        const text = await response.text();
        setContent(text);
      } catch (err) {
        console.error(err);
        setError('Failed to load markdown content.');
      } finally {
        setLoading(false);
      }
    };

    if (viewerUrl) {
      fetchContent();
    }
  }, [viewerUrl]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
  };

  if (loading) {
    return (
      <motion.div
        key="skeleton"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{ width: '100%' }}
      >
        <ContentSkeleton />
      </motion.div>
    );
  }

  if (error) {
    return (
      <div className={styles.centerContainer}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <motion.div
      className={styles.container}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* <motion.div className={styles.header} variants={itemVariants}>
        <div className={styles.fileInfo}>
          <FileText size={18} className={styles.icon} />
          <span className={styles.title}>
            {contentDetails?.metadata?.file_metadata?.name || 'Markdown Document'}
          </span>
        </div>
        <motion.button
          onClick={handleCopy}
          className={styles.copyButton}
          aria-label="Copy content"
          title="Copy content"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {copied ? (
              <motion.span
                key="copied"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <Check size={16} className={styles.successIcon} />
                <span>Copied</span>
              </motion.span>
            ) : (
              <motion.span
                key="copy"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <Copy size={16} />
                <span>Copy</span>
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </motion.div>*/}

      <ContentHeader
        title={
          contentDetails?.title ||
          contentDetails?.metadata?.file_metadata?.name ||
          contentDetails?.file_name ||
          'Markdown Document'
        }
        url={viewerUrl}
        config={contentDetails?.metadata?.configurations || {}}
      />
      
      <motion.div className={styles.contentWrapper} variants={itemVariants}>
        <ContentMarkdownRenderer content={content} />
      </motion.div>
    </motion.div>
  );
};

export const ContentMarkdownRenderer = ({ content }) => {
  const H1 = ({ node, children, ...props }) => (
    <h1 className={styles['md-h1']} {...props}>
      {children}
    </h1>
  );
  
  const H2 = ({ node, children, ...props }) => (
    <h2 className={styles['md-h2']} {...props}>
      {children}
    </h2>
  );

  const H3 = ({ node, children, ...props }) => (
    <h3 className={styles['md-h3']} {...props}>
      {children}
    </h3>
  );

  const Table = ({ node, ...props }) => (
    <table className={styles['md-table']} {...props} />
  );
  const Th = ({ node, ...props }) => (
    <th className={styles['md-th']} {...props} />
  );
  const Td = ({ node, ...props }) => (
    <td className={styles['md-td']} {...props} />
  );
  const Tr = ({ node, ...props }) => (
    <tr className={styles['md-tr']} {...props} />
  );

  const Ul = ({ node, ...props }) => (
    <ul className={styles['md-ul']} {...props} />
  );
  const Ol = ({ node, ...props }) => (
    <ol className={styles['md-ol']} {...props} />
  );

  const Li = ({ node, ...props }) => (
    <li className={styles['md-li']} {...props} />
  );

  const P = ({ node, ...props }) => <p className={styles['md-p']} {...props} />;
  const A = ({ node, children, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className={styles['md-a']}
    >
      {children}
    </a>
  );

  const Pre = ({ node, ...props }) => (
    <pre className={styles['md-pre']} {...props} />
  );

  const Code = ({ node, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const isBlock = match || String(children).includes('\n');
    if (isBlock) {
      return (
        <SyntaxHighlighter
          style={theme}
          language={match?.[1] || 'text'}
          PreTag="div"
          className={styles['code-syntax-highliter']}
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      );
    }

    return (
      <code className={styles['inline-code']} {...props}>
        {children}
      </code>
    );
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        h1: H1,
        h2: H2,
        h3: H3,
        table: Table,
        th: Th,
        td: Td,
        tr: Tr,
        ul: Ul,
        ol: Ol,
        li: Li,
        a: A,
        p: P,
        code: Code,
        pre: Pre,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;